import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Whether the feedback panel is open — nothing more.
 *
 * It lives in a context rather than in a page because four unrelated places
 * open the same panel: the nav rail, the top bar, the wizard's own header (the
 * wizard has its own chrome and no top bar), and the `/feedback` deep link
 * from the invitation email. Pure client state, one flag; server state belongs
 * nowhere near it.
 *
 * `source` is kept only so the panel can tell a deep-link arrival from a click
 * — the deep link opens over whatever page the person landed on.
 */

export type FeedbackSource = 'top-bar' | 'wizard' | 'link';

interface FeedbackContextValue {
  open: boolean;
  source: FeedbackSource | null;
  openFeedback: (source?: FeedbackSource) => void;
  closeFeedback: () => void;
}

const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined);

export const useFeedback = (): FeedbackContextValue => {
  const context = useContext(FeedbackContext);
  if (!context) throw new Error('useFeedback must be used inside a FeedbackProvider');
  return context;
};

export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [source, setSource] = useState<FeedbackSource | null>(null);

  const openFeedback = useCallback((from: FeedbackSource = 'top-bar') => setSource(from), []);
  const closeFeedback = useCallback(() => setSource(null), []);

  const value = useMemo(
    () => ({ open: source !== null, source, openFeedback, closeFeedback }),
    [source, openFeedback, closeFeedback]
  );

  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
};

export default FeedbackContext;
